import {
  IPackagingProcessConfiguration,
  IPackagingProcessingActivity,
} from '../modules/packaging-pcf/processing-activities/process/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface IState {
  packagingProcess: IPackagingProcessingActivity
  loading: boolean
  error: string
  configuration: IPackagingProcessConfiguration
}

const initialState: IState = {
  configuration: {
    regions: [],
    countries: [],
    inputIngredientTypes: [],
    outputProductTypes: [],
    resourceSources: [],
    secondaryDataReferenceSourceType: [],
    packagingMaterialsTaxonomies: {
      classes: [],
      categories: [],
      functionalUnits: [],
    },
    packagingComponents: [],
    materialEmissionImpacts: [],
  },
  packagingProcess: {
    code: null,
    lastPublishedDate: '',
    researcherName: '',
    researcherEmail: '',
    lastUpdatedName: null,
    updated: '',
    state: 0,
    activityName: '',
    processFlow: '',
    materials: [],
    resourceSources: [],
    elementaryFlows: [],
    packagingComponentId: null,
    componentScalingFactor: 100,
    waste: [],
    outputElementaryFlows: [],
    additionalSupplementaryInformation: '',
    secondaryDataReferenceSourceType: null,
    dataQualityReliabilityScore: null,
    dataQualityCompletenessScore: null,
    referencesSamplingYear: null,
    referenceDataPublicationYear: null,
    referenceRegionIdentifier: null,
    dataQualityComment: '',
    referenceSourcingCountry: null,
    companyId: null,
    referenceTechnologyType: null,
    referenceTechnologyProxyContribution: null,
    isVerified: false,
    verificationComments: null,
    verifiedOn: null,
    verifiedByEmail: null,
    verifiedByName: null,
    defaultMaterialLossFactor: 1,
    isEndOfLife: false,
  },
  loading: true,
  error: '',
}

const packagingProcessSlice = createSlice({
  name: 'packagingProcess',
  initialState,
  reducers: {
    packagingProcessRequest(state) {
      state.loading = true
    },
    packagingProcessSuccess(state, action: PayloadAction<IPackagingProcessingActivity>) {
      state.loading = false
      state.packagingProcess = action.payload
    },
    packagingProcessFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },
    configuration(state, action: PayloadAction<IPackagingProcessConfiguration>) {
      // materialEmissionImpacts is BE-blocked until the material-emission-factors module ships
      // (see capability-map.md) - default to empty so the warning lookup never crashes on undefined.
      state.configuration = {
        ...action.payload,
        materialEmissionImpacts: action.payload.materialEmissionImpacts ?? [],
      }
    },
    handlePublishStatus(state, action: PayloadAction<number>) {
      state.packagingProcess.state = action.payload
    },
    addNote(state, action) {
      // Indexes state.packagingProcess, not state.process — the food slice's easy copy-paste miss.
      if (action.payload.index != undefined) {
        if (
          state.packagingProcess[action.payload.list][action.payload.index][action.payload.key] ==
          null
        ) {
          state.packagingProcess[action.payload.list][action.payload.index][action.payload.key] = []
        }
        state.packagingProcess[action.payload.list][action.payload.index][action.payload.key].push(
          action.payload.value
        )
      } else {
        if (state.packagingProcess[action.payload.key] == null) {
          state.packagingProcess[action.payload.key] = []
        }
        state.packagingProcess[action.payload.key].push(action.payload.value)
      }
    },
    setDefaultPackagingProcess: () => initialState,
  },
})

export default packagingProcessSlice.reducer
export const {
  packagingProcessRequest,
  packagingProcessSuccess,
  packagingProcessFailure,
  handlePublishStatus,
  setDefaultPackagingProcess,
  addNote,
  configuration,
} = packagingProcessSlice.actions
